import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';

/** Same blue already used on the startup dashboard. */
const POPUP_BLUE = '#2B9FEF';

interface ProfileReadyPopupProps {
  isOpen: boolean;
  onClose: () => void;
}

/**
 * Shown on the profile view so the founder can see their live profile behind it.
 * Keep the scrim light — not a heavy blackout.
 */
const ProfileReadyPopup: React.FC<ProfileReadyPopupProps> = ({ isOpen, onClose }) => {
  useEffect(() => {
    if (!isOpen) return;
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return createPortal(
    <div
      className="fixed inset-0 bg-black/15 flex items-center justify-center z-[100000] p-4"
      onClick={event => {
        if (event.target === event.currentTarget) onClose();
      }}
      role="dialog"
      aria-modal="true"
      aria-labelledby="profile-ready-title"
    >
      <div
        className="relative z-10 border border-gray-700 rounded-2xl shadow-xl max-w-lg w-full px-8 pb-8 pt-6 text-center"
        style={{ backgroundColor: '#1E1E1C', fontFamily: 'Rubik, sans-serif' }}
        onClick={event => event.stopPropagation()}
      >
        <button
          type="button"
          onClick={onClose}
          className="absolute right-3 top-3 p-1.5 rounded-md text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M6 18L18 6M6 6l12 12"
            />
          </svg>
        </button>
        <h2 id="profile-ready-title" className="text-white text-lg sm:text-xl font-medium mb-4">
          Here is how investors see your profile.
        </h2>
        <p className="text-white text-sm sm:text-base mb-4">
          We&apos;ll notify you whenever an investor is interested.
        </p>
        <p className="text-sm sm:text-base font-medium" style={{ color: POPUP_BLUE }}>
          Now go back to building a great company ☺
        </p>
      </div>
    </div>,
    document.body
  );
};

export default ProfileReadyPopup;
